import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import {
  fetchTeachersForTimetable,
  getAllSubjects,
  buildTimetableDesignContext,
  type Teacher,
} from './aiTimetableService';

/**
 * Fetches school data from Firestore to provide context to AI
 */

// Re-export timetable service functions for convenience
export { fetchTeachersForTimetable, getAllSubjects, buildTimetableDesignContext };
export type { Teacher };

// Fetch all timetables for a specific class
// Path: /school-users/{tenantId}/timetables/{class}{section}_{day}
// Example: class12a_wednesday, class12b_friday
export const fetchTimetablesForClass = async (domain: string, className?: string) => {
  try {
    console.log('🔍 Fetching timetables for domain:', domain);
    // Timetables are stored in school-users collection
    const timetablesRef = collection(db, `school-users/${domain}/timetables`);

    if (className) {
      // Filter by class if provided
      const classPattern = className.toLowerCase().replace('-', '').replace(' ', '');
      const snapshot = await getDocs(timetablesRef);
      console.log('📊 Timetables found:', snapshot.size);
      const timetables = snapshot.docs
        .filter(doc => doc.id.toLowerCase().includes(classPattern))
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
      return timetables;
    } else {
      const snapshot = await getDocs(timetablesRef);
      console.log('📊 Timetables found:', snapshot.size);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
    }
  } catch (error) {
    console.error('❌ Error fetching timetables:', error);
    return [];
  }
};

// Fetch all classes
export const fetchClasses = async (domain: string) => {
  try {
    console.log('🔍 Fetching classes for domain:', domain);
    const classesRef = collection(db, `school-data/${domain}/classes`);
    const snapshot = await getDocs(classesRef);
    console.log('📊 Classes found:', snapshot.size);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('❌ Error fetching classes:', error);
    return [];
  }
};

// Fetch all teachers (from school-users collection where teachers are stored)
export const fetchTeachers = async (domain: string) => {
  try {
    console.log('🔍 Fetching teachers for domain:', domain);
    // Teachers are stored in school-users collection
    const teachersRef = collection(db, `school-users/${domain}/teachers`);
    const snapshot = await getDocs(teachersRef);
    console.log('📊 Teachers found:', snapshot.size);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        teacherId: data.teacherId || doc.id,
        name: data.name || '',
        subject: data.subject || '',
        subjects: data.subjects || [data.subject].filter(Boolean),
        department: data.department || '',
        email: data.email || '',
        phone: data.phone || '',
        qualification: data.qualification || '',
        ...data,
      };
    });
  } catch (error) {
    console.error('❌ Error fetching teachers:', error);
    return [];
  }
};

// Fetch schedules/holidays
export const fetchSchedules = async (domain: string) => {
  try {
    console.log('🔍 Fetching schedules for domain:', domain);
    const schedulesRef = collection(db, `school-data/${domain}/schedules`);
    const snapshot = await getDocs(schedulesRef);
    console.log('📊 Schedules found:', snapshot.size);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('❌ Error fetching schedules:', error);
    return [];
  }
};

// Fetch holidays
export const fetchHolidays = async (domain: string) => {
  try {
    console.log('🔍 Fetching holidays for domain:', domain);
    const holidaysRef = collection(db, `school-data/${domain}/holidays`);
    const snapshot = await getDocs(holidaysRef);
    console.log('📊 Holidays found:', snapshot.size);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('❌ Error fetching holidays:', error);
    return [];
  }
};

// Fetch student statistics
export const fetchStudentStats = async (domain: string) => {
  try {
    console.log('🔍 Fetching student stats for domain:', domain);
    const studentsRef = collection(db, `school-users/${domain}/students`);
    const snapshot = await getDocs(studentsRef);
    console.log('📊 Students found:', snapshot.size);

    const stats = {
      totalStudents: snapshot.size,
      byClass: {} as Record<string, number>,
      bySection: {} as Record<string, number>,
    };

    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const className = data.class || data.className || 'Unknown';
      const section = data.section || 'Unknown';

      stats.byClass[className] = (stats.byClass[className] || 0) + 1;
      stats.bySection[section] = (stats.bySection[section] || 0) + 1;
    });

    return stats;
  } catch (error) {
    console.error('❌ Error fetching student stats:', error);
    return { totalStudents: 0, byClass: {}, bySection: {} };
  }
};

// Fetch exam results summary
export const fetchExamResultsSummary = async (domain: string, className?: string) => {
  try {
    const examResultsRef = collection(db, `school-data/${domain}/exam-results`);
    const snapshot = await getDocs(examResultsRef);

    const exams = snapshot.docs.map(doc => ({
      examId: doc.id,
      ...doc.data(),
    }));

    if (className) {
      return exams.filter((exam: any) =>
        exam.className?.toLowerCase().includes(className.toLowerCase())
      );
    }

    return exams;
  } catch (error) {
    console.error('Error fetching exam results:', error);
    return [];
  }
};

// Build comprehensive context for AI
export const buildSchoolContext = async (domain: string, specificClass?: string) => {
  try {
    console.log('🏫 Building school context for domain:', domain);
    const [classes, teachers, timetables, schedules, holidays, studentStats] = await Promise.all([
      fetchClasses(domain),
      fetchTeachers(domain),
      fetchTimetablesForClass(domain, specificClass),
      fetchSchedules(domain),
      fetchHolidays(domain),
      fetchStudentStats(domain),
    ]);

    console.log('✅ Context built successfully:', {
      classes: classes.length,
      teachers: teachers.length,
      timetables: timetables.length,
      schedules: schedules.length,
      holidays: holidays.length,
      students: studentStats.totalStudents
    });

    const context = {
      school: {
        domain,
        totalClasses: classes.length,
        totalTeachers: teachers.length,
        totalStudents: studentStats.totalStudents,
      },
      classes: classes.map((c: any) => ({
        id: c.id,
        name: c.name || c.className,
        students: studentStats.byClass[c.id] || 0,
      })),
      teachers: teachers.map((t: any) => ({
        id: t.teacherId || t.id,
        teacherId: t.teacherId || t.id,
        name: t.name,
        subject: t.subject,
        subjects: t.subjects || [t.subject].filter(Boolean),
        department: t.department,
        email: t.email,
        phone: t.phone,
      })),
      timetables: timetables.map((tt: any) => ({
        id: tt.id,
        class: tt.className || extractClassFromId(tt.id),
        day: tt.day || extractDayFromId(tt.id),
        periods: tt.periods || [],
      })),
      schedules: schedules.length,
      holidays: holidays.length,
      studentDistribution: studentStats.byClass,
    };

    return context;
  } catch (error) {
    console.error('Error building school context:', error);
    return null;
  }
};

// Helper function to extract class from timetable ID
const extractClassFromId = (id: string): string => {
  const match = id.match(/class(\d+[a-z]?)/i);
  return match ? match[1] : 'Unknown';
};

// Helper function to extract day from timetable ID
const extractDayFromId = (id: string): string => {
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const lowerID = id.toLowerCase();
  const foundDay = days.find(day => lowerID.includes(day));
  return foundDay ? foundDay.charAt(0).toUpperCase() + foundDay.slice(1) : 'Unknown';
};

// Format context as readable text for AI
export const formatContextForAI = (context: any): string => {
  if (!context) return '';

  let formatted = `\n\n📊 SCHOOL DATA CONTEXT:\n\n`;
  formatted += `🏫 School Overview:\n`;
  formatted += `- Domain: ${context.school.domain}\n`;
  formatted += `- Total Classes: ${context.school.totalClasses}\n`;
  formatted += `- Total Teachers: ${context.school.totalTeachers}\n`;
  formatted += `- Total Students: ${context.school.totalStudents}\n\n`;

  if (context.classes.length > 0) {
    formatted += `📚 Classes (${context.classes.length}):\n`;
    context.classes.slice(0, 10).forEach((cls: any) => {
      formatted += `- ${cls.id}: ${cls.students} students\n`;
    });
    if (context.classes.length > 10) {
      formatted += `... and ${context.classes.length - 10} more classes\n`;
    }
    formatted += `\n`;
  }

  if (context.teachers.length > 0) {
    // Group teachers by subject for easier scheduling
    const teachersBySubject: Record<string, any[]> = {};
    context.teachers.forEach((teacher: any) => {
      const subjects = teacher.subjects || [teacher.subject].filter(Boolean);
      subjects.forEach((subject: string) => {
        if (subject) {
          const subjectKey = subject.toLowerCase().trim();
          if (!teachersBySubject[subjectKey]) {
            teachersBySubject[subjectKey] = [];
          }
          teachersBySubject[subjectKey].push(teacher);
        }
      });
    });

    formatted += `👨‍🏫 TEACHERS LIST (${context.teachers.length} teachers):\n`;
    formatted += `| Teacher ID | Name | Subject(s) | Department |\n`;
    formatted += `|------------|------|------------|------------|\n`;
    context.teachers.forEach((teacher: any) => {
      const subjects = teacher.subjects?.join(', ') || teacher.subject || 'N/A';
      formatted += `| ${teacher.teacherId || teacher.id} | ${teacher.name} | ${subjects} | ${teacher.department || 'N/A'} |\n`;
    });
    formatted += `\n`;

    // Subject-to-Teacher mapping for quick lookup
    formatted += `📚 SUBJECT-WISE TEACHER MAPPING (use this for scheduling):\n`;
    Object.entries(teachersBySubject).sort().forEach(([subject, teachers]) => {
      const teacherNames = teachers.map((t: any) => `${t.name} (${t.teacherId || t.id})`).join(', ');
      formatted += `• ${subject.charAt(0).toUpperCase() + subject.slice(1)}: ${teacherNames}\n`;
    });
    formatted += `\n`;

    formatted += `💡 SCHEDULING INSTRUCTIONS:\n`;
    formatted += `- When asked to create a timetable, use the ACTUAL teachers from above\n`;
    formatted += `- Match subjects to teachers based on their expertise\n`;
    formatted += `- Include Teacher ID in the timetable for reference\n`;
    formatted += `- Standard periods: 8 periods/day (45 mins each) + breaks\n`;
    formatted += `\n`;
  }

  if (context.timetables.length > 0) {
    formatted += `📅 Timetables with Period Details (${context.timetables.length}):\n`;
    context.timetables.forEach((tt: any) => {
      formatted += `\n📋 ${tt.id} (${tt.class} - ${tt.day}):\n`;
      if (tt.periods && tt.periods.length > 0) {
        tt.periods.forEach((period: any, index: number) => {
          const startTime = period.startTime || period.start_time || 'N/A';
          const endTime = period.endTime || period.end_time || 'N/A';
          const subject = period.subject || period.subjectName || 'N/A';
          const teacher = period.teacher || period.teacherName || 'N/A';
          const room = period.room || period.roomNumber || '';
          formatted += `  Period ${index + 1}: ${startTime} - ${endTime} | ${subject} | ${teacher}${room ? ` | Room: ${room}` : ''}\n`;
        });
      } else {
        formatted += `  No period details available\n`;
      }
    });
    formatted += `\n`;
  }

  formatted += `📈 Student Distribution by Class:\n`;
  Object.entries(context.studentDistribution).slice(0, 10).forEach(([className, count]) => {
    formatted += `- ${className}: ${count} students\n`;
  });

  formatted += `\n✨ Use this data to provide context-aware suggestions!\n\n`;

  return formatted;
};
